import { PaymentProvider } from "./../../../types/tenantConfig";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { EquipmentListProps, KeyHandoverServiceType, LocationPros } from "../../../types/baseTypes";
import {
  BraintreePaymentToken,
  ParcelLockerGroup,
  // CouponAppliedReservation,
  PersonalData,
  PersonalDataValidation,
  Reservation,
  ReservationSnippet,
  SearchRoomDataValidation,
  SomeoneElseData,
  SomeoneElseDataValidation,
  StripeToken,
  VerifyPinResponseData,
  VippsToken,
} from "../../../types/bookingCreation";
import moment from "moment";
import { Order, OrderId } from "../../../types/order";
import { getEnumNameByNumber, getEnumNumberByName } from "../../../util/enum";
import {
  cleanEmail,
  isEmailAcceptable,
  isPlausiblePhone,
  isValidPhone,
  isValidUscCustomerId,
} from "../../../util/validation";
import { isPlausibleName } from "../../../util/personalDataRules";
import { requiredPersonalDataFields } from "../../../util/requiredPersonalDataFields";
import { flowUseCase } from "../../../util/bookingFlowUseCase";
import { spaceOf } from "../../../util/useCases";
import DefaultConfig from "../../../util/defaultConfig";
import {
  AssetGroupAvailability,
  SUPAssetGroupAvailability,
} from "../../../types/assetGroup";

export enum BookingCreationERStep {
  locationSelection,
  assetGroupSelection,
  orderData,
  confirmation,
  personalData,
  paymentTypeSelection,
  payment,
}

export enum BookingCreationP2PStep {
  locationSelection,
  paymentModelSelection,
  assetGroupSelection,
  orderData,
  // landlordData,
  personalData,
  recipientData,
  confirmation,
  // paymentTypeSelection,
  payment,
}

export enum BookingCreationSUPStep {
  locationSelection,
  assetGroupSelection,
  orderData,
  confirmation,
  personalData,
  paymentTypeSelection,
  payment,
}

export interface BookingCreationStartedPayload {
  locationId: string;
}
export interface BookingFlowPayload {
  bookingFlow: string[];
}

export interface DateSelectedPayload {
  date: moment.Moment | null;
}

export interface CheckInDateSelectedPayload {
  checkInDate: moment.Moment | null;
}

export interface CheckOutDateSelectedPayload {
  checkOutDate: moment.Moment | null;
}

export interface NumberOfGuestsSelectedPayload {
  numberOfGuests: number;
}

export interface QuantitySelectedPayload {
  quantity: number;
}

export interface TimeSelectedPayload {
  time: string;
}

export interface DurationSelectedPayload {
  duration: number;
}

export interface AssetCountSelectedPayload {
  assetCount: number;
}

export interface AssetGroupActivatedPayload {
  id: string;
}

export interface AssetGroupPayload {
  id: string;
}

export interface AssetGroupDeactivatedPayload {
  id: string;
}

export interface AssetGroupCountChangedPayload {
  id: string;
  count: number;
}

export interface PaymentTypeChangedPayload {
  paymentType: string;
}

export interface ReservationCreatedPayload {
  reservation: Reservation;
}

export interface ReservationSnippetCreatedPayload {
  reservation: ReservationSnippet;
}

export interface ReservationUpdatedPayload {
  reservation: Reservation;
}

export interface ReservationRemovedPayload {
  reservationId?: string;
}

export interface FirstNameChangedPayload {
  firstName: string;
}

export interface LastNameChangedPayload {
  lastName: string;
}

export interface SomeoneElseFirstNameChangedPayload {
  firstName: string;
}

export interface SomeoneElseLastNameChangedPayload {
  lastName: string;
}

export interface SomeoneElseEmailChangedPayload {
  email: string;
}

export interface PhoneChangedPayload {
  phone: string;
}

export interface EmailChangedPayload {
  email: string;
}

export interface AgreementChangedPayload {
  isAgreed: boolean;
}

export interface NewsletterConsentChangedPayload {
  newsletterConsent: boolean;
}

export interface UscCustomerIdChangedPayload {
  uscCustomerId: string;
}

export interface PersonalDataChangedPayload {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  newsletterConsent: boolean;
  uscCustomerId?: string;
}

export interface CouponRedemptionLoadingPayload {
  loading: boolean;
}

export interface BookingCreationCanceledPayload {}

export interface BraintreePaymentInitializedPayload {
  braintreePaymentToken: BraintreePaymentToken;
}

export interface VippsPaymentInitializedPayload {
  vippsToken: VippsToken;
  checkoutFrontendUrl: string;
}
export interface StripePaymentInitializedPayload {
  token: StripeToken;
  checkoutFrontendUrl: string;
}

export interface NoPaymentInitializedPayload {
  orderIds: string[];
  reservationIds: string[];
  statusCode: string;
}

export interface PaymentInitializationFailedPayload {}

export interface PaymentInitializationRetryStartedPayload {}

export interface PaymentCompletionBeganPayload {}

export interface PaymentCompletedPayload {
  orderId: OrderId;
  orderIds?: OrderId[];
  orders?: Order[];
}

export interface PaymentCompletionFailedPayload {}

export interface BookingStepChangedPayload {
  step: number;
  nextEnabled: boolean;
}

export interface BookingCreationCompletedPayload {}

export interface BookingCreationSelection {
  date: moment.Moment | null;
  checkInDate: moment.Moment | null;
  checkOutDate: moment.Moment | null;
  // Keys flow: optional second period for key return step
  keyReturnFromDate?: moment.Moment | null;
  keyReturnToDate?: moment.Moment | null;
  time: string;
  duration: number;
  amount: number;
  numberOfGuests: number;
  quantity: number;
  quantity2?: number;
  startTime?: string;
  endTime?: string;
  eventType?: string | null;
  numberOfDays?: number;
  packageSize?: string;
  comment?: string;
  /**
   * Which of the customer's organizations covers this booking (#1926).
   *
   * Only ever set when they hold more than one approved membership and the
   * room does not settle the question by itself — with a single membership
   * there is nothing to ask. A preference, not an assertion: the server
   * honours it only if the session actually holds that membership.
   */
  coveringOrganizationId?: string | null;
  /**
   * The member chose "Privat buchen – ich zahle selbst": this booking is not
   * covered, even though one of their organizations covers the room. Every
   * booking starts on the organization (PO, 2026-10-20); the server reads it
   * as `bookPrivately` and only ever declines with it.
   *
   * Holds the id of the ROOM the choice was made for, not a bare flag: a
   * member who goes back and picks another covered room starts on the
   * organization again. Read only through `selectBookPrivately`.
   */
  bookPrivatelyFor?: string | null;
  /**
   * #1810: the cost centre the member TYPED on a covered booking. Undefined
   * while they have not touched the field — the app then sends nothing and
   * the server stores the organisation's (unit's) default, the same value the
   * field shows. Blank is "none".
   */
  costCenter?: string;
}

export interface GroupsByLocation {
  [locationId: string]: EquipmentListProps[];
}

export interface GroupsByLocationPayload {
  locationId: string;
  groups: EquipmentListProps[];
}

export interface UseCasePayload {
  // null is dispatched by useBookingFlowLifecycle on dialog unmount to
  // prevent the previous flow's useCase from leaking into the next one.
  useCase: string | null;
}

export interface AdditionalService {
  serviceId: string;
  name: string;
  quantity: number; // -1 means no quantity for the service
}

export interface RemoveService {
  name: string;
}

export interface SomeoneElse {
  isForSomeoneElse: boolean;
}

export interface RoomRentalVoucher {
  id: string;
  code: string;
  discountIsPercentage: boolean;
  discountValue: number;
}

export interface RoomRentalLocation {
  city: string | null;
  district: string | null;
}

export interface AddAnotherEquipmentPayload {}

export interface BookingCreationState {
  locationId: string | null;
  multipleBoardTypes: boolean;
  isAddingAnotherEquipment: boolean;
  partner: "usc" | null;
  step: number;
  nextEnabled: boolean;
  /**
   * Is the booking being made right now covered by the customer's organization
   * (#1926)? Mirrored into this slice by the shared customer-data step, because
   * the fact lives in the apartment slice (`selectedApartment.coveredForMe`)
   * and a reducer cannot reach across slices. The server is the authority: it
   * re-derives the same predicate when it creates the order, so this only ever
   * decides what we ASK for, never what is charged.
   */
  coveredBooking: boolean;
  /**
   * Has the IndexedDB draft been read into this slice in this page session?
   * The personal-data step remounts on every entry; re-reading the draft on a
   * remount would overwrite the newer Redux values with a draft the debounced
   * save may not have caught up with. A refresh resets the slice, so the draft
   * is read exactly once per page load.
   */
  personalDataDraftRestored: boolean;
  /**
   * The account whose profile was last copied into `personalData`. Persisted
   * with the IndexedDB draft, so the prefill runs once per account and draft —
   * not on every step entry or refresh, where it overwrote the customer's own
   * edits with the profile values.
   */
  accountPrefillAppliedFor: string | null;
  orderData: BookingCreationSelection;
  assetGroupId: string | null;
  availabilities: AssetGroupAvailability | null;
  supAvailabilities: SUPAssetGroupAvailability | null;
  personalData: PersonalData;
  landlordData: PersonalData;
  recipientData: PersonalData;
  someoneElseData: SomeoneElseData;
  personalDataValidation: PersonalDataValidation;
  landlordDataValidation: PersonalDataValidation;
  recipientDataValidation: PersonalDataValidation;
  someoneElseDataValidation: SomeoneElseDataValidation;
  reservations: Reservation[];
  reservationsSnippets?: ReservationSnippet[];
  payment: {
    provider: "braintree" | "vipps" | "stripe" | "no_payment" | null;
    paymentType: string;
    paymentInitFailed: boolean;
    // orderId: null | OrderId;
    orderIds?: OrderId[] | null;
    subscriptionId?: string | null;
    orders?: Order[] | null;
    braintree: {
      paymentToken: null | BraintreePaymentToken;
    };
    vipps: {
      token: null | VippsToken;
      checkoutFrontendUrl: null | string;
    };
    stripe: {
      token: null | StripeToken;
      checkoutFrontendUrl: null | string;
    };
  };
  appliedCouponCode: string | null;
  loading: {
    couponRedemption: boolean;
    paymentCompletion: boolean;
  };
  bookingFlow: string[];
  selectedLocation: LocationPros | null;
  selectedCity: string | null;
  hasSelectedLocation: boolean;
  selectedGroup: EquipmentListProps | null;
  groupsByLocation: GroupsByLocation;
  /**
   * The location whose groups failed to load (#1970). Held per location so a
   * selection step only reports a failure for the location it is showing, and
   * so moving to another location cannot inherit a stale error.
   */
  groupsLoadFailedLocationId: string | null;
  useCase: string | null;
  additionalServices: AdditionalService[];
  isForSomeoneElse: boolean;
  roomRentalVoucher: RoomRentalVoucher | null;
  totalPrice: number;
  roomRentalLocation: RoomRentalLocation;
  loader: boolean;
  voucherSelectedAmount: string | null;
  voucherCustomAmount: string;
  voucherAmount: number;
  voucherCustomAmountIsValid: boolean;
  keyHandoverSelectedService : KeyHandoverServiceType
  verifyPinResponseData: VerifyPinResponseData | null;
  verificationPin: string | null;
  selectedParcelLocker: ParcelLockerGroup | null;
  // Keys: track which sub-phase the reused recipientData step is in
  keyRecipientPhase: "return" | "guest" | null;
  keyHandoverSelectedPaymentModel: 'subscription-new' | 'subscription-existing' | 'single-booking' | null;
  // Keys: track if user has explicitly selected pickup time
  hasSelectedPickupTime: boolean;
  hasSelectedReturnTime: boolean;
}

const initialState: BookingCreationState = {
  locationId: null,
  multipleBoardTypes: false,
  isAddingAnotherEquipment: false,
  partner: null,
  step: 0,
  nextEnabled: false,
  // Not covered is the shape every booking had before #1926, so it is also the
  // safe default when the availability route carried no service and the server
  // could not answer.
  coveredBooking: false,
  personalDataDraftRestored: false,
  accountPrefillAppliedFor: null,
  orderData: {
    date: null,
    checkInDate: null,
    checkOutDate: null,
    keyReturnFromDate: null,
    keyReturnToDate: null,
    time: "0",
    duration: 0,
    amount: 1,
    numberOfGuests: 1,
    quantity: 1,
    quantity2: 1,
    startTime: "0",
    endTime: "0",
    eventType: null,
    numberOfDays: 0,
    comment: "",
    coveringOrganizationId: null,
    bookPrivatelyFor: null,
  },
  assetGroupId: null,
  availabilities: null,
  supAvailabilities: null,
  personalData: {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    newsletterConsent: false,
    isAgreed: false,
    company: "",
    address: "",
    zipCode: "",
    city: "",
    country: "",
  },
  landlordData: {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    newsletterConsent: false,
    isAgreed: false,
  },
  recipientData: {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    newsletterConsent: false,
    isAgreed: false,
  },
  someoneElseData: {
    firstName: "",
    lastName: "",
    email: "",
  },
  personalDataValidation: {
    firstName: false,
    lastName: false,
    phone: false,
    email: false,
    uscCustomerId: false,
    isAgreed: false,
    company: false,
    address: false,
    zipCode: false,
    city: false,
    country: false,
  },
  landlordDataValidation: {
    firstName: false,
    lastName: false,
    phone: false,
    email: false,
    uscCustomerId: false,
    isAgreed: false,
    company: false,
    address: false,
    zipCode: false,
    city: false,
    country: false,
  },
  someoneElseDataValidation: {
    firstName: false,
    lastName: false,
    email: false,
  },
  recipientDataValidation: {
    firstName: false,
    lastName: false,
    phone: false,
    email: false,
    uscCustomerId: false,
    isAgreed: false,
    company: false,
    address: false,
    zipCode: false,
    city: false,
    country: false,
  },
  reservations: [],
  payment: {
    provider: null,
    paymentType: "",
    paymentInitFailed: false,
    orderIds: null,
    subscriptionId: null,
    braintree: {
      paymentToken: null,
    },
    vipps: {
      token: null,
      checkoutFrontendUrl: null,
    },
    stripe: {
      token: null,
      checkoutFrontendUrl: null,
    },
  },
  appliedCouponCode: null,
  loading: {
    couponRedemption: false,
    paymentCompletion: false,
  },
  bookingFlow: [],
  selectedLocation: null,
  selectedCity: null,
  hasSelectedLocation: false,
  selectedGroup: null,
  groupsByLocation: {},
  groupsLoadFailedLocationId: null,
  useCase: null,
  additionalServices: [],
  isForSomeoneElse: false,
  roomRentalVoucher: null,
  totalPrice: 0,
  roomRentalLocation: {
    city: null,
    district: null,
  },
  loader: false,
  voucherSelectedAmount: null,
  voucherCustomAmount: "",
  voucherAmount: 0,
  voucherCustomAmountIsValid: false,
  keyHandoverSelectedService: "Key-Pickup-Only",
  verifyPinResponseData: null,
  verificationPin: null,
  selectedParcelLocker: null,
  keyRecipientPhase: null,
  keyHandoverSelectedPaymentModel: null,
  hasSelectedPickupTime: false,
  hasSelectedReturnTime: false,
};

const bookingCreationSlice = createSlice({
  name: "bookingCreation",
  initialState,
  reducers: {
    setOrderComment: (state, action: PayloadAction<string>) => {
      state.orderData.comment = action.payload;
    },
    bookingFlowChanged: (state, action: PayloadAction<BookingFlowPayload>) => {
      state.bookingFlow = [...action.payload.bookingFlow];
    },
    setKeyRecipientPhase: (state, action: PayloadAction<"return" | "guest" | null>) => {
      state.keyRecipientPhase = action.payload;
    },
    setKeyPaymentModel: (state, action: PayloadAction<'subscription-new' | 'subscription-existing' | 'single-booking' | null>) => {
      state.keyHandoverSelectedPaymentModel = action.payload;
    },
    setPickupTimeSelected: (state, action: PayloadAction<boolean>) => {
      state.hasSelectedPickupTime = action.payload;
    },
    setReturnTimeSelected: (state, action: PayloadAction<boolean>) => {
      state.hasSelectedReturnTime = action.payload;
    },
    // Keys flow: select key return period (second date range)
    keyReturnFromDateSelected: (
      state,
      action: PayloadAction<CheckInDateSelectedPayload>
    ) => {
      state.nextEnabled = false;
      state.orderData.keyReturnFromDate = action.payload.checkInDate ?? null;
    },
    keyReturnToDateSelected: (
      state,
      action: PayloadAction<CheckOutDateSelectedPayload>
    ) => {
      state.nextEnabled = false;
      state.orderData.keyReturnToDate = action.payload.checkOutDate ?? null;
    },
    bookingCreationStarted: (
      state,
      action: PayloadAction<BookingCreationStartedPayload>
    ) => {
      state.locationId = action.payload.locationId;
      state.personalData = {
        ...initialState.personalData,
      };
    },
    bookingCreationCanceled: () => initialState,
    setNextEnabled: (state, action: PayloadAction<boolean>) => {
      state.nextEnabled = action.payload;
    },

    setAvailabilities: (
      state,
      action: PayloadAction<AssetGroupAvailability>
    ) => {
      state.availabilities = action.payload;
    },
    setSUPAvailabilities: (
      state,
      action: PayloadAction<SUPAssetGroupAvailability>
    ) => {
      state.supAvailabilities = action.payload;
    },
    navigatedToNextStep: (state) => {
      // Custom logic for navigating to the next step
      const paymentProvider = DefaultConfig.paymentProvider;

      // Handle parcel flow specifically
      if (state.useCase === "parcel") {
        const parcelSteps = [0, 1, 2, 3, 4, 5]; // hints, locationSelection, packageDetails, personalData, bookingSummary, success
        const currentStepIndex = parcelSteps.indexOf(state.step);

        if (currentStepIndex !== -1 && currentStepIndex < parcelSteps.length - 1) {
          state.step = parcelSteps[currentStepIndex + 1];

          // Enable next button for all steps except the last one
          state.nextEnabled = currentStepIndex + 1 < parcelSteps.length - 1;

          // For personalData step, check if personal data is valid
          if (state.step === 3) { // personalData step
            state.nextEnabled = isPersonalDataValid(
              validatePersonalData(
                state.personalData,
                state.useCase,
                state.coveredBooking
              ),
              state.useCase,
              state.coveredBooking
            );
          }

          return;
        }
      }

      // Handle voucher flow: simple step increment (0=orderData, 1=bookingSummary, 2=payment)
      if (state.useCase === "voucher") {
        const voucherSteps = [0, 1, 2];
        const currentStepIndex = voucherSteps.indexOf(state.step);
        if (currentStepIndex !== -1 && currentStepIndex < voucherSteps.length - 1) {
          state.step = voucherSteps[currentStepIndex + 1];
          state.nextEnabled = currentStepIndex + 1 < voucherSteps.length - 1;
        }
        return;
      }

      let nextStep = state.step;
      do {
        nextStep++;
        if (state.step === BookingCreationERStep.locationSelection) {
          // Do nothing
        } else if (state.step === BookingCreationERStep.assetGroupSelection) {
          // Do nothing
        } else if (state.step === BookingCreationERStep.confirmation) {
          // Do nothing
        } else if (
          state.useCase === "rental" &&
          state.step === BookingCreationERStep.personalData &&
          paymentProvider &&
          paymentProvider.length === 1
        ) {
          state.payment.provider = paymentProvider[0] as PaymentProvider;

          nextStep = BookingCreationERStep.payment;
        } else if (
          state.useCase === "sup" &&
          state.step === BookingCreationSUPStep.personalData &&
          paymentProvider &&
          paymentProvider.length === 1
        ) {
          state.payment.provider = paymentProvider[0] as PaymentProvider;

          nextStep = BookingCreationSUPStep.payment;
        } else if (
          state.useCase === "p2p" &&
          state.step === BookingCreationP2PStep.personalData &&
          paymentProvider &&
          paymentProvider.length === 1
        ) {
          state.payment.provider = paymentProvider[0] as PaymentProvider;

          nextStep = BookingCreationP2PStep.payment;
        }

        const nextStepName = getEnumNameByNumber(
          state.useCase === "rental" || state.useCase === "sup"
            ? BookingCreationERStep
            : BookingCreationP2PStep,
          nextStep
        );
        if (!nextStepName) {
          throw new Error("Invalid step");
        }
        // Do not break here; allow the loop to continue until we find the next step present in bookingFlow
      } while (
        state.bookingFlow.indexOf(
          getEnumNameByNumber(
            state.useCase === "rental" || state.useCase === "sup"
              ? BookingCreationERStep
              : BookingCreationP2PStep,
            nextStep
          )
        ) === -1
      );

      state.step = nextStep;

      const isConfirmationStep =
        (nextStep === BookingCreationERStep.confirmation &&
          state.useCase === "rental") ||
        (nextStep === BookingCreationERStep.confirmation &&
          state.useCase === "sup") ||
        (nextStep === BookingCreationP2PStep.confirmation &&
          state.useCase === "p2p");

      const isPersonalDataStep =
        (nextStep === BookingCreationERStep.personalData ||
          nextStep === BookingCreationP2PStep.personalData) &&
        state.useCase !== null &&
        isPersonalDataValid(
          validatePersonalData(
            state.personalData,
            state.useCase,
            state.coveredBooking
          ),
          state.useCase,
          state.coveredBooking
        );

      state.nextEnabled = isConfirmationStep || isPersonalDataStep;
    },
    navigatedToPreviousStep: (state) => {
      const bookingFlow = state.bookingFlow;
      let currentStepIndex;

      // Handle parcel flow specifically
      if (state.useCase === "parcel") {
        const parcelSteps = [0, 1, 2, 3, 4, 5]; // hints, locationSelection, packageDetails, personalData, bookingSummary, success
        const currentStepIndex = parcelSteps.indexOf(state.step);
        
        if (currentStepIndex > 0) {
          state.step = parcelSteps[currentStepIndex - 1];
          state.nextEnabled = true;
          return;
        }
      }

      if (state.useCase === "voucher") {
        // For voucher flow, just decrement the step
        state.step = state.step - 1;
        state.nextEnabled = true;
        return;
      }

      if (state.useCase === "rental" || state.useCase === "sup") {
        currentStepIndex = bookingFlow.indexOf(
          getEnumNameByNumber(BookingCreationERStep, state.step)
        );
      } else {
        currentStepIndex = bookingFlow.indexOf(
          getEnumNameByNumber(BookingCreationP2PStep, state.step)
        );
      }

      const previousStepName = bookingFlow[currentStepIndex - 1];

      let previousStep;
      if (state.useCase === "rental" || state.useCase === "sup") {
        if (state.isAddingAnotherEquipment) {
          previousStep = BookingCreationERStep.confirmation;
          state.isAddingAnotherEquipment = false;
        } else {
          previousStep = getEnumNumberByName(
            BookingCreationERStep,
            previousStepName
          );
        }
      } else {
        previousStep = getEnumNumberByName(
          BookingCreationP2PStep,
          previousStepName
        );
      }
      const resetBoardSelection = previousStep === 0;

      state.step = previousStep;
      state.assetGroupId = resetBoardSelection
        ? initialState.assetGroupId
        : state.assetGroupId;
      state.nextEnabled = true;
    },
    dateSelected: (state, action: PayloadAction<DateSelectedPayload>) => {
      state.nextEnabled = false;
      state.orderData = {
        ...initialState.orderData,
        date: action.payload.date,
      };
    },
    dateSelectedClear: (state) => {
      state.orderData = {
        ...initialState.orderData,
        date: null,
      };
    },
    checkInDateSelected: (
      state,
      action: PayloadAction<CheckInDateSelectedPayload>
    ) => {
      state.orderData.checkInDate = action.payload.checkInDate;
      if (state.orderData.checkInDate && state.orderData.checkOutDate) {
        const diff = state.orderData.checkOutDate.diff(
          state.orderData.checkInDate,
          "days"
        );
        state.orderData.numberOfDays = diff > 0 ? diff : 0;
      } else {
        state.orderData.numberOfDays = 0;
      }
      state.nextEnabled = isLocationDurationValid(state);
    },
    checkInDateSelectedClear: (state) => {
      state.orderData.checkInDate = null;
      state.nextEnabled = false;
    },
    checkOutDateSelected: (
      state,
      action: PayloadAction<CheckOutDateSelectedPayload>
    ) => {
      state.orderData.checkOutDate = action.payload.checkOutDate;
      if (state.orderData.checkInDate && state.orderData.checkOutDate) {
        const diff = state.orderData.checkOutDate.diff(
          state.orderData.checkInDate,
          "days"
        );
        state.orderData.numberOfDays = diff > 0 ? diff : 0;
      } else {
        state.orderData.numberOfDays = 0;
      }
      state.nextEnabled = isLocationDurationValid(state);
    },
    checkOutDateSelectedClear: (state) => {
      state.orderData.checkOutDate = null;
      state.nextEnabled = false;
    },

    numberOfGuestsSelected: (
      state,
      action: PayloadAction<NumberOfGuestsSelectedPayload>
    ) => {
      state.orderData.numberOfGuests = action.payload.numberOfGuests;
      state.nextEnabled = isLocationDurationValid(state);
    },
    numberOfGuestsSelectedClear: (state) => {
      state.orderData = {
        ...initialState.orderData,
        numberOfGuests: 1,
      };
    },
    timeSelected: (state, action: PayloadAction<TimeSelectedPayload>) => {
      state.nextEnabled = false;
      state.orderData = {
        ...state.orderData,
        time: action.payload.time,
        duration: 0,
        amount: 1,
      };
    },
    durationSelected: (
      state,
      action: PayloadAction<DurationSelectedPayload>
    ) => {
      state.nextEnabled = Boolean(action.payload.duration);
      state.orderData = {
        ...state.orderData,
        duration: action.payload.duration,
        amount: 1,
      };
    },
    durationSelectedClear: (state) => {
      state.orderData = {
        ...state.orderData,
        duration: 0,
        amount: 0,
      };
    },
    packageSizeSelected: (
      state,
      action: PayloadAction<EquipmentListProps>
    ) => {
      if (!state.selectedLocation) {  
        return;
      }

      const packageSize = state.groupsByLocation[state.selectedLocation?.id].find(
        (group) => group.id === action.payload.id
      );

      if (!packageSize) {
        return;
      }

      state.orderData = {
        ...state.orderData,
        packageSize: packageSize.name,
      };
    },
    quantitySelected: (
      state,
      action: PayloadAction<QuantitySelectedPayload>
    ) => {
      state.nextEnabled = true;
      state.orderData = {
        ...state.orderData,
        quantity: action.payload.quantity,
      };
    },
    quantity2Selected: (
      state,
      action: PayloadAction<QuantitySelectedPayload>
    ) => {
      state.nextEnabled = true;
      state.orderData = {
        ...state.orderData,
        quantity2: action.payload.quantity,
      };
    },
    quantitySelectedClear: (state) => {
      state.orderData = {
        ...initialState.orderData,
        quantity: 1,
        quantity2: 1
      };
    },

    setVoucherCustomAmount: (state, action) => {
      state.voucherCustomAmount = action.payload;
    },
    setVoucherSelectedAmount: (state, action: PayloadAction<string>) => {
      state.voucherSelectedAmount = action.payload;
    },
    clearVoucherSelectedAmount: (state) => {
      state.voucherSelectedAmount = "10";
    },
    clearVoucherCustomAmount: (state) => {
      state.voucherCustomAmount = "";
    },
    setVoucherAmount: (state, action) => {
      state.voucherAmount = action.payload;
    },
    setVoucherCustomAmountIsValid: (state, action) => {
      state.voucherCustomAmountIsValid = action.payload;
    },
    assetCountSelected: (
      state,
      action: PayloadAction<AssetCountSelectedPayload>
    ) => {
      state.orderData.amount = action.payload.assetCount;
    },
    setAssetGroup: (state, action: PayloadAction<AssetGroupPayload>) => {
      state.assetGroupId = action.payload.id;
    },
    assetGroupActivated: (
      state,
      action: PayloadAction<AssetGroupActivatedPayload>
    ) => {
      state.assetGroupId = action.payload.id;
      state.nextEnabled = true;
    },
    assetGroupDeactivated: (state) => {
      state.assetGroupId = null;
      state.selectedGroup = null;
      state.nextEnabled = false;
    },
    assetGroupCountChanged: (
      state,
      action: PayloadAction<AssetGroupCountChangedPayload>
    ) => {
      state.assetGroupId = action.payload.id;
      state.nextEnabled =
        state.step === 1 ? action.payload.count >= 1 : state.nextEnabled;
    },
    reservationCreated: (
      state,
      action: PayloadAction<ReservationCreatedPayload>
    ) => {
      const newReservations = [
        ...state.reservations,
        action.payload.reservation,
      ];
      state.reservations = newReservations;
    },
    reservationSnippetCreated: (
      state,
      action: PayloadAction<ReservationSnippetCreatedPayload>
    ) => {
      const newReservations = [
        ...state.reservations,
        action.payload.reservation,
      ];
      state.reservationsSnippets = newReservations;
    },
    reservationUpdated: (
      state,
      action: PayloadAction<ReservationUpdatedPayload>
    ) => {
      const updatedReservation = action.payload.reservation;
      state.reservations = state.reservations.map((r) =>
        r.id === updatedReservation.id ? updatedReservation : r
      );
    },
    reservationRemoved: (
      state,
      action: PayloadAction<ReservationRemovedPayload>
    ) => {
      if (!action.payload.reservationId) {
        state.reservations.pop();
      } else {
        const reservationId = action.payload.reservationId;

        const index = state.reservations.findIndex(
          (reservation) => reservation.reservationId === reservationId
        );

        if (index !== -1) {
          state.reservations.splice(index, 1);
        }
      }
    },
    couponAppliedToReservations: (
      state,
      action: PayloadAction<Reservation[]>
    ) => {
      const allReservations = state.reservations.map((reservation) => {
        const foundReservation = action.payload.find(
          (r) => r.id === reservation.reservationId
        );

        if (foundReservation) {
          return {
            ...reservation,
            payment: {
              ...reservation.payment,
              ...foundReservation.payment,
              totalPrice: reservation.payment.totalPrice, // Preserve the original totalPrice
            },
          };
        }

        return reservation;
      });

      state.reservations = allReservations;
    },
    setAppliedCouponCode: (state, action: PayloadAction<string>) => {
      state.appliedCouponCode = action.payload;
    },
    firstNameChanged: (
      state,
      action: PayloadAction<FirstNameChangedPayload>
    ) => {
      state.personalData.firstName = action.payload.firstName;
      recomputePersonalData(state);
    },
    lastNameChanged: (state, action: PayloadAction<LastNameChangedPayload>) => {
      state.personalData.lastName = action.payload.lastName;
      recomputePersonalData(state);
    },
    phoneChanged: (state, action: PayloadAction<PhoneChangedPayload>) => {
      const trimmed = action.payload.phone.trim();
      
      // If only country code (e.g., "+49"), treat as empty
      if (/^\+\d{0,3}$/.test(trimmed)) {
        state.personalData.phone = "";
      } else if (trimmed !== "" && !trimmed.startsWith("+")) {
        state.personalData.phone = `+${trimmed}`;
      } else {
        state.personalData.phone = trimmed;
      }
      
      recomputePersonalData(state);
    },
    emailChanged: (state, action: PayloadAction<EmailChangedPayload>) => {
      state.personalData.email = cleanEmail(action.payload.email);
      recomputePersonalData(state);
    },
    companyNameChanged: (
      state,
      action: PayloadAction<{ company: string }>
    ) => {
      state.personalData.company = action.payload.company;
      recomputePersonalData(state);
    },
    addressChanged: (state, action: PayloadAction<{ address: string }>) => {
      state.personalData.address = action.payload.address;
      recomputePersonalData(state);
    },
    zipCodeChanged: (
      state,
      action: PayloadAction<{ zipCode: string }>
    ) => {
      state.personalData.zipCode = action.payload.zipCode;
      recomputePersonalData(state);
    },
    cityChanged: (state, action: PayloadAction<{ city: string }>) => {
      state.personalData.city = action.payload.city;
      recomputePersonalData(state);
    },
    countryChanged: (state, action: PayloadAction<{ country: string }>) => {
      state.personalData.country = action.payload.country;
      recomputePersonalData(state);
    },
    recipientFirstNameChanged: (
      state,
      action: PayloadAction<FirstNameChangedPayload>
    ) => {
      state.recipientData.firstName = action.payload.firstName;
      state.recipientDataValidation = validatePersonalData(
        state.recipientData,
        state.useCase
      );
      state.nextEnabled = isRecipientDataValid(state.recipientDataValidation);
    },
    recipientLastNameChanged: (
      state,
      action: PayloadAction<LastNameChangedPayload>
    ) => {
      state.recipientData.lastName = action.payload.lastName;
      state.recipientDataValidation = validatePersonalData(
        state.recipientData,
        state.useCase
      );
      state.nextEnabled = isRecipientDataValid(state.recipientDataValidation);
    },
    recipientPhoneChanged: (
      state,
      action: PayloadAction<PhoneChangedPayload>
    ) => {
      const trimmed = action.payload.phone.trim();
      
      // If only country code (e.g., "+49"), treat as empty
      if (/^\+\d{0,3}$/.test(trimmed)) {
        state.recipientData.phone = "";
      } else if (trimmed !== "" && !trimmed.startsWith("+")) {
        state.recipientData.phone = `+${trimmed}`;
      } else {
        state.recipientData.phone = trimmed;
      }
      
      state.recipientDataValidation = validatePersonalData(
        state.recipientData,
        state.useCase
      );
      state.nextEnabled = isRecipientDataValid(state.recipientDataValidation);
    },
    recipientEmailChanged: (
      state,
      action: PayloadAction<EmailChangedPayload>
    ) => {
      state.recipientData.email = cleanEmail(action.payload.email);
      state.recipientDataValidation = validatePersonalData(
        state.recipientData,
        state.useCase
      );
      state.nextEnabled = isRecipientDataValid(state.recipientDataValidation);
    },
    // Landlord data handlers (separate early step)
    landlordFirstNameChanged: (
      state,
      action: PayloadAction<FirstNameChangedPayload>
    ) => {
      state.landlordData.firstName = action.payload.firstName;
      state.landlordDataValidation = validatePersonalData(
        state.landlordData,
        state.useCase,
        true // phone is required for landlord
      );
      state.nextEnabled = isLandlordDataValid(state.landlordDataValidation);
    },
    landlordLastNameChanged: (
      state,
      action: PayloadAction<LastNameChangedPayload>
    ) => {
      state.landlordData.lastName = action.payload.lastName;
      state.landlordDataValidation = validatePersonalData(
        state.landlordData,
        state.useCase,
        true // phone is required for landlord
      );
      state.nextEnabled = isLandlordDataValid(state.landlordDataValidation);
    },
    landlordPhoneChanged: (
      state,
      action: PayloadAction<PhoneChangedPayload>
    ) => {
      const trimmed = action.payload.phone.trim();
      
      // For landlord, phone is required - keep the value as-is (including country code only)
      if (trimmed !== "" && !trimmed.startsWith("+")) {
        state.landlordData.phone = `+${trimmed}`;
      } else {
        state.landlordData.phone = trimmed;
      }
      
      state.landlordDataValidation = validatePersonalData(
        state.landlordData,
        state.useCase,
        true // phone is required for landlord
      );
      state.nextEnabled = isLandlordDataValid(state.landlordDataValidation);
    },
    landlordEmailChanged: (
      state,
      action: PayloadAction<EmailChangedPayload>
    ) => {
      state.landlordData.email = cleanEmail(action.payload.email);
      state.landlordDataValidation = validatePersonalData(
        state.landlordData,
        state.useCase,
        true // phone is required for landlord
      );
      state.nextEnabled = isLandlordDataValid(state.landlordDataValidation);
    },
    agreementChanged: (
      state,
      action: PayloadAction<AgreementChangedPayload>
    ) => {
      state.personalData.isAgreed = action.payload.isAgreed;
      recomputePersonalData(state);
    },
    newsletterConsentChanged: (
      state,
      action: PayloadAction<NewsletterConsentChangedPayload>
    ) => {
      state.personalData.newsletterConsent = action.payload.newsletterConsent;
    },
    paymentTypeChanged: (
      state,
      action: PayloadAction<PaymentTypeChangedPayload>
    ) => {
      state.payment.paymentType = action.payload.paymentType;
    },
    uscCustomerIdChanged: (
      state,
      action: PayloadAction<UscCustomerIdChangedPayload>
    ) => {
      state.personalData.uscCustomerId = action.payload.uscCustomerId;
      recomputePersonalData(state);
    },
    personalDataChanged: (
      state,
      action: PayloadAction<PersonalDataChangedPayload>
    ) => {
      state.personalData = {
        ...state.personalData,
        ...action.payload,
      };
      // Validation only — this reducer deliberately does NOT touch nextEnabled
      // (it did not before, and a bulk hydrate should not enable a button the
      // customer has not earned). It still has to pass `coveredBooking`, or a
      // covered booking would paint its four empty address fields red right
      // after being filled from a draft.
      state.personalDataValidation = validatePersonalData(
        state.personalData,
        state.useCase,
        state.coveredBooking
      );
    },
    /**
     * Recompute personalDataValidation and nextEnabled from the CURRENT
     * personal data. Dispatched by the shared customer-data step on mount so
     * every flow's next button reflects the current rules on step entry —
     * including flows whose own step slices (storage, room/venue) don't
     * recompute nextEnabled when the step changes, and previously persisted
     * drafts that violate rules introduced after they were saved.
     */
    personalDataRevalidated: (state) => {
      recomputePersonalData(state);
    },
    /**
     * Mirror the covered-booking fact into this slice (#1926). Dispatched by
     * the shared customer-data step, which can read it via
     * `useIsCoveredBooking` and this reducer cannot.
     *
     * It revalidates, because the answer changes WHICH fields are required: a
     * booking that becomes covered must stop demanding a billing address, and
     * one that stops being covered must start again — without waiting for the
     * customer to touch a field.
     */
    coveredBookingChanged: (
      state,
      action: PayloadAction<{ covered: boolean }>
    ) => {
      if (state.coveredBooking === action.payload.covered) return;
      state.coveredBooking = action.payload.covered;
      recomputePersonalData(state);
    },
    personalDataHydrated: (
      state,
      action: PayloadAction<Partial<PersonalData>>
    ) => {
      state.personalData = {
        ...state.personalData,
        ...action.payload,
      };
      recomputePersonalData(state);
    },
    /**
     * The IndexedDB draft has been read (or there was none). Dispatched once
     * per page load by `usePersonalDataPersistence`; see
     * `personalDataDraftRestored` on the state.
     */
    personalDataDraftLoaded: (
      state,
      action: PayloadAction<{
        data: Partial<PersonalData> | null;
        accountPrefillAppliedFor: string | null;
      }>
    ) => {
      state.personalDataDraftRestored = true;
      if (!action.payload.data) return;
      state.personalData = {
        ...state.personalData,
        ...action.payload.data,
      };
      state.accountPrefillAppliedFor = action.payload.accountPrefillAppliedFor;
      recomputePersonalData(state);
    },
    /**
     * A signed-in account's non-empty profile fields were copied into the
     * form. Recorded per account so the copy is not repeated over the
     * customer's later edits.
     */
    accountProfilePrefilled: (
      state,
      action: PayloadAction<{
        accountId: string;
        prefill: Partial<PersonalData>;
      }>
    ) => {
      state.accountPrefillAppliedFor = action.payload.accountId;
      state.personalData = {
        ...state.personalData,
        ...action.payload.prefill,
      };
      recomputePersonalData(state);
    },
    /**
     * H-3a scenario 4 (#1873): wipe the personal data on account-session END
     * (explicit logout, GDPR delete, 401 session expiry) so the next user of
     * the device never sees the previous user's PII prefilled in the booking
     * form. Deliberately does NOT touch `nextEnabled`/`step` — the user is
     * never on the personal-data step when a session-end event fires, and the
     * step revalidates on mount (`personalDataRevalidated`).
     */
    personalDataCleared: (state) => {
      state.personalData = {
        ...initialState.personalData,
      };
      state.personalDataValidation = {
        ...initialState.personalDataValidation,
      };
      state.accountPrefillAppliedFor = null;
    },
    someoneElseFirstNameChanged: (
      state,
      action: PayloadAction<SomeoneElseFirstNameChangedPayload>
    ) => {
      state.someoneElseData.firstName = action.payload.firstName;
      state.someoneElseDataValidation = validateSomeoneElseData(
        state.someoneElseData,
        state.useCase
      );
      state.nextEnabled = isPersonalDataStepValid(state);
    },
    someoneElseLastNameChanged: (
      state,
      action: PayloadAction<SomeoneElseLastNameChangedPayload>
    ) => {
      state.someoneElseData.lastName = action.payload.lastName;
      state.someoneElseDataValidation = validateSomeoneElseData(
        state.someoneElseData,
        state.useCase
      );
      state.nextEnabled = isPersonalDataStepValid(state);
    },
    someoneElseEmailChanged: (
      state,
      action: PayloadAction<SomeoneElseEmailChangedPayload>
    ) => {
      state.someoneElseData.email = cleanEmail(action.payload.email);
      state.someoneElseDataValidation = validateSomeoneElseData(
        state.someoneElseData,
        state.useCase
      );
      state.nextEnabled = isPersonalDataStepValid(state);
    },
    couponRedemptionLoading: (
      state,
      action: PayloadAction<CouponRedemptionLoadingPayload>
    ) => {
      state.loading.couponRedemption = action.payload.loading;
    },
    initializePaymentProvider: (
      state,
      action: PayloadAction<PaymentProvider | null>
    ) => {
      state.payment.provider = action.payload;
    },
    braintreePaymentInitialized: (
      state,
      action: PayloadAction<BraintreePaymentInitializedPayload>
    ) => {
      state.payment.provider = "braintree";
      state.payment.braintree = {
        paymentToken: action.payload.braintreePaymentToken,
      };
    },
    vippsPaymentInitialized: (
      state,
      action: PayloadAction<VippsPaymentInitializedPayload>
    ) => {
      state.payment.provider = "vipps";
      state.payment.vipps = {
        token: action.payload.vippsToken,
        checkoutFrontendUrl: action.payload.checkoutFrontendUrl,
      };
    },
    stripePaymentInitialized: (
      state,
      action: PayloadAction<StripePaymentInitializedPayload>
    ) => {
      state.payment.provider = "stripe";
      state.payment.stripe = {
        token: action.payload.token,
        checkoutFrontendUrl: action.payload.checkoutFrontendUrl,
      };
    },
    noPaymentInitialized: (state) => {
      state.payment.provider = "no_payment";
    },
    completeStripePayment(
      state,
      action: PayloadAction<{ reservationId: string }>
    ) {},
    paymentInitializationFailed: (
      state,
      action: PayloadAction<PaymentInitializationFailedPayload>
    ) => {
      state.payment.paymentInitFailed = true;
    },
    paymentInitializationRetryStarted: (
      state,
      action: PayloadAction<PaymentInitializationRetryStartedPayload>
    ) => {
      state.payment.paymentInitFailed = false;
    },
    paymentCompletionBegan: (state) => {
      state.loading.paymentCompletion = true;
    },
    setIsAddingAnotherEquipment: (state, action: PayloadAction<boolean>) => {
      state.isAddingAnotherEquipment = action.payload;
    },
    paymentCompleted: (state, action: PayloadAction<string[]>) => {
      state.payment.orderIds = action.payload;
      state.loading.paymentCompletion = false;
    },
    subscriptionCompleted: (state, action: PayloadAction<string>) => {
      state.payment.subscriptionId = action.payload;
      state.loading.paymentCompletion = false;
    },
    paymentCompletionFailed: (state) => {
      state.loading.paymentCompletion = false;
    },
    bookingStepChanged: (
      state,
      action: PayloadAction<BookingStepChangedPayload>
    ) => {
      state.step = action.payload.step;
      state.nextEnabled = action.payload.nextEnabled;
    },
    /**
     * Re-ask "is the location-and-duration step complete?" from current state.
     *
     * QA #2793: a deep link arrives with city, district, both dates, both
     * times and the event type all hydrated and visibly filled in — and
     * "Weiter" is dead until the customer touches any one field.
     *
     * `nextEnabled` was only ever recomputed by the INPUT reducers
     * (`checkInDateSelected`, `numberOfGuestsSelected`, …), i.e. by the
     * customer typing. URL hydration fills the same fields through other
     * paths, so the validator never ran on what it filled in. The shell's
     * mount effect did guess a value, but from `selectedLocation` alone —
     * dates and times do not appear in it at all.
     *
     * Takes no payload on purpose: the answer is a function of state, and a
     * caller that could pass its own would be a second opinion about a
     * question `isLocationDurationValid` already owns. Same reasoning as the
     * overview step's invariant (#2764 E2) — state the rule, do not make it
     * depend on who dispatched last.
     */
    locationDurationRevalidated: (state) => {
      state.nextEnabled = isLocationDurationValid(state);
    },
    bookingCreationCompleted: () => initialState,
    setSelectedLocation: (
      state,
      action: PayloadAction<LocationPros | null>
    ) => {
      state.selectedLocation = action.payload;
      state.selectedGroup = null;
    },
    setRoomRentalLocation: (
      state,
      action: PayloadAction<RoomRentalLocation>
    ) => {
      state.roomRentalLocation = action.payload;
      state.nextEnabled = isLocationDurationValid(state);
    },
    setSelectedGroup: (
      state,
      action: PayloadAction<EquipmentListProps | null>
    ) => {
      state.selectedGroup = action.payload;
    },
    setHasSelectedLocation: (state, action: PayloadAction<boolean>) => {
      state.hasSelectedLocation = action.payload;
      // Re-evaluate nextEnabled based on the current state
      if (state.hasSelectedLocation) {
        if (spaceOf(flowUseCase(state.useCase))?.flow === "stay") {
          state.nextEnabled = isSearchRoomDataValid({
            city: true,
            district: true,
            checkInDate: state.orderData.checkInDate !== null,
            checkOutDate: state.orderData.checkOutDate !== null,
            numberOfGuests: true,
          });
        } else {
          state.nextEnabled = true;
        }
      } else {
        state.nextEnabled = false;
      }
    },
    addAnotherGroup: (
      state,
      action: PayloadAction<AddAnotherEquipmentPayload>
    ) => {
      state.orderData = {
        ...initialState.orderData,
      };
      state.nextEnabled = false;
      if (state.useCase === "sup") {
        state.step = 2;
      } else {
        state.step = 1;
        state.assetGroupId = null;
        state.selectedGroup = null;
      }
      state.personalData = {
        ...initialState.personalData,
      };
      state.personalDataValidation = {
        ...initialState.personalDataValidation,
      };
      // The step refills from the IndexedDB draft on its next entry, as it
      // did before the draft was read once per page load.
      state.personalDataDraftRestored = false;
      state.accountPrefillAppliedFor = null;
    },
    setGroupsByLocation: (
      state,
      action: PayloadAction<GroupsByLocationPayload>
    ) => {
      const locationId = action.payload.locationId;
      const groups = action.payload.groups;
      if (!state.groupsByLocation[locationId]) {
        state.groupsByLocation[locationId] = groups;
      }
      if (state.groupsLoadFailedLocationId === locationId) {
        state.groupsLoadFailedLocationId = null;
      }
    },
    // #1970: the selection steps cannot tell a failed load from a slow one —
    // they derive "loading" from the list being empty — so the failure has to
    // be recorded for them to render instead of spinning forever.
    groupsLoadStarted: (state) => {
      state.groupsLoadFailedLocationId = null;
    },
    groupsLoadFailed: (state, action: PayloadAction<string>) => {
      state.groupsLoadFailedLocationId = action.payload;
    },
    setUseCase: (state, action: PayloadAction<UseCasePayload>) => {
      // "Booking for someone else" belongs to the flow it was chosen in. Left
      // set, a later booking in another flow would be sent with that person
      // as its recipient (`bookingRecipient`).
      if (state.useCase !== action.payload.useCase) {
        state.isForSomeoneElse = false;
        state.someoneElseData = { ...initialState.someoneElseData };
        state.someoneElseDataValidation = {
          ...initialState.someoneElseDataValidation,
        };
      }
      state.useCase = action.payload.useCase;
    },
    addService: (state, action: PayloadAction<AdditionalService>) => {
      state.additionalServices.push(action.payload);
    },
    editService: (state, action: PayloadAction<AdditionalService>) => {
      const index = state.additionalServices.findIndex(
        (service) => service.name === action.payload.name
      );
      if (index !== -1) {
        state.additionalServices[index] = {
          ...state.additionalServices[index],
          ...action.payload,
        };
      }
    },
    removeService: (state, action: PayloadAction<RemoveService>) => {
      state.additionalServices = state.additionalServices.filter(
        (item) => item.name !== action.payload.name
      );
    },
    setForSomeoneElse: (state, action: PayloadAction<SomeoneElse>) => {
      state.isForSomeoneElse = action.payload.isForSomeoneElse;
      state.nextEnabled = isPersonalDataStepValid(state);
    },
    setRoomRentalVoucher: (state, action: PayloadAction<RoomRentalVoucher>) => {
      state.roomRentalVoucher = action.payload;
    },
    setTotalPrice: (state, action: PayloadAction<number>) => {
      state.totalPrice = action.payload;
    },
    setLoader: (state, action: PayloadAction<boolean>) => {
      state.loader = action.payload;
    },
    startTimeSelected: (
      state,
      action: PayloadAction<{ startTime: string }>
    ) => {
      state.orderData.startTime = action.payload.startTime;
      state.nextEnabled = isLocationDurationValid(state);
    },
    endTimeSelected: (state, action: PayloadAction<{ endTime: string }>) => {
      // Defence-in-depth (review §4): refuse endTime <= startTime once both
      // are set. UI-side BookingTimeSelect already filters its options, but
      // hand-edited URLs and future hydration paths can supply inverted
      // ranges that the slice should reject silently rather than store.
      // Only enforced for same-day bookings — when check-out is a later day
      // than check-in, an end time-of-day earlier than the start time is a
      // perfectly valid range (e.g. Aug 1 17:00 → Aug 2 13:00).
      // Compare numerically — string comparison would treat "18:00" < "9:00"
      // because the codebase generates unpadded single-digit hour slots
      // (see generateHourSlots in util/index.ts).
      const newEnd = action.payload.endTime;
      const start = state.orderData.startTime ?? "0";
      const { checkInDate, checkOutDate } = state.orderData;
      const isMultiDay =
        checkInDate !== null &&
        checkOutDate !== null &&
        !checkOutDate.isSame(checkInDate, "day");
      const isLater = (a: string, b: string): boolean => {
        const [ah, am] = a.split(":").map(Number);
        const [bh, bm] = b.split(":").map(Number);
        return ah * 60 + am > bh * 60 + bm;
      };
      if (
        newEnd === "0" ||
        start === "0" ||
        isMultiDay ||
        isLater(newEnd, start)
      ) {
        state.orderData.endTime = newEnd;
      }
      state.nextEnabled = isLocationDurationValid(state);
    },
    setKeyHandoverSelectedService: (state, action: PayloadAction<KeyHandoverServiceType>) => {
      state.keyHandoverSelectedService = action.payload;
    },
    eventTypeSelected: (
      state,
      action: PayloadAction<{ eventType: string | null }>
    ) => {
      state.orderData.eventType = action.payload.eventType;
      state.nextEnabled = isLocationDurationValid(state);
    },
    coveringOrganizationSelected: (
      state,
      action: PayloadAction<{ organizationId: string | null }>
    ) => {
      // #1810: a typed cost centre belongs to the organisation it was typed
      // for; another organisation starts again from its own default.
      if (state.orderData.coveringOrganizationId !== action.payload.organizationId) {
        state.orderData.costCenter = undefined;
      }
      state.orderData.coveringOrganizationId = action.payload.organizationId;
      state.orderData.bookPrivatelyFor = null;
    },
    /** "Privat buchen – ich zahle selbst" (docs/plans/member-private-booking.md). */
    privateBookingChosen: (state, action: PayloadAction<{ roomId: string }>) => {
      state.orderData.bookPrivatelyFor = action.payload.roomId;
      state.orderData.coveringOrganizationId = null;
      state.orderData.costCenter = undefined;
    },
    costCenterTyped: (state, action: PayloadAction<{ costCenter: string }>) => {
      state.orderData.costCenter = action.payload.costCenter;
    },
    setVerifyPinResponseData: (state, action: PayloadAction<VerifyPinResponseData>) => {
      state.verifyPinResponseData = action.payload;
    },
    setSelectedParcelLocker: (state, action: PayloadAction<ParcelLockerGroup>) => {
      state.selectedParcelLocker = action.payload;
    },
    setVerificationPin: (state, action: PayloadAction<string>) => {
      state.verificationPin = action.payload;
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
  },
});

// @todo validating usc customer id like that is probably something that easily breaks at some point
// @todo this function should actually get the information whether this is a USC booking or not
/**
 * `phoneRequired` was removed rather than kept: it was declared, defaulted to
 * `false` and NEVER read in the body — phone was validated unconditionally
 * regardless of what any caller passed. A parameter that silently does nothing
 * is worse than no parameter, so it is gone; whether phone is demanded is a
 * question for `requiredPersonalDataFields`, which the gate below consults.
 */
const validatePersonalData = (
  data: PersonalData,
  useCase: string | null,
  covered: boolean = false
): PersonalDataValidation => {
  // Rigorous rules on the base fields (util/personalDataRules.ts): mandatory
  // personal data must be plausible, not merely non-empty. Applies to every
  // flow — "required ⇒ rigorously validated" (product decision, #1401/ADR-0005).
  const firstNameValid = isPlausibleName(data.firstName);
  const lastNameValid = isPlausibleName(data.lastName);
  // Phone is always required: must be non-empty AND valid AND not an obvious
  // dummy pattern (repeated digit cycles, all-same-digit).
  const phoneValid =
    data.phone.length > 0 &&
    isValidPhone(data.phone) &&
    isPlausiblePhone(data.phone);
  // isEmailAcceptable also rejects known disposable domains.
  const emailValid = data.email.length > 0 && isEmailAcceptable(data.email);
  const uscCustomerIdValid =
    data.uscCustomerId === undefined ||
    (data.uscCustomerId.trim().length > 0 &&
      isValidUscCustomerId(data.uscCustomerId.trim()));
  const isAgreedValid = useCase === "rental" ? data.isAgreed === true : true;
  // Company is now optional: valid if empty or non-empty
  const companyValid = data.company === undefined || data.company.length >= 0;
  // The address block is valid-when-empty exactly when this booking does not
  // require it — otherwise a covered booking would render four red fields the
  // customer is not being asked to fill. Same set the gate below checks and the
  // form renders from, so the asterisk, the error and the Next button cannot
  // disagree (they did: see `util/requiredPersonalDataFields.ts`).
  const addressRequired = requiredPersonalDataFields(useCase, { covered }).has(
    "address"
  );
  // TRIMMED, because the reducers no longer do it on the way in (#2948).
  // Without this a single space would count as a filled address: the store
  // used to strip it on every keystroke, so `length > 0` was safe by accident.
  const filled = (value: string | undefined) =>
    !addressRequired || (!!value && value.trim().length > 0);
  const addressValid = filled(data.address);
  const zipCodeValid = filled(data.zipCode);
  const cityValid = filled(data.city);
  const countryValid = filled(data.country);
  return {
    firstName: firstNameValid,
    lastName: lastNameValid,
    phone: phoneValid,
    email: emailValid,
    uscCustomerId: uscCustomerIdValid,
    isAgreed: isAgreedValid,
    company: companyValid,
    address: addressValid,
    zipCode: zipCodeValid,
    city: cityValid,
    country: countryValid,
  };
};

const validateSomeoneElseData = (
  data: SomeoneElseData,
  useCase: string | null
): SomeoneElseDataValidation => {
  // Trimmed for the same reason as `filled` above (#2948).
  return {
    firstName: data.firstName.trim().length > 0,
    lastName: data.lastName.trim().length > 0,
    email: data.email.length > 0 && isEmailAcceptable(data.email),
  };
};

/**
 * May the customer leave the personal-data step?
 *
 * The required field set is NOT restated here — it comes from
 * `requiredPersonalDataFields`, the same definition the form renders from
 * (`util/requiredPersonalDataFields.ts`). Before that function existed this
 * one hardcoded address + zipCode + city + country for `"room"` and `"venue"`,
 * which is why #1926's covered-booking narrowing removed the asterisks and
 * left the Next button disabled: two answers to one question, and the one that
 * gated was the one nobody was configuring.
 *
 * `uscCustomerId` and `isAgreed` are checked unconditionally, as they were
 * before: neither is a field the customer can be asked for more or less of —
 * the first is gated on the runtime USC partner signal and is `undefined`
 * (⇒ valid) otherwise, the second is a use-case rule.
 */
const isPersonalDataValid = (
  validation: PersonalDataValidation,
  service: string | null,
  covered: boolean = false
): boolean => {
  const required = requiredPersonalDataFields(service, { covered });
  const satisfied = Array.from(required).every((field) => validation[field]);
  return satisfied && validation.uscCustomerId && validation.isAgreed;
};

/**
 * Re-derive the customer-data validation AND the next button from the data
 * currently in state.
 *
 * Extracted because thirteen reducers wrote the identical pair, and the pair is
 * the thing that must not drift: `nextEnabled` is only meaningful against the
 * validation it was computed from. Threading the covered flag through thirteen
 * copies by hand is exactly how the two would have come apart again.
 */
/**
 * WHY THESE REDUCERS NO LONGER TRIM (#2948)
 *
 * They used to write `action.payload.x.trim()` on EVERY keystroke. The fields
 * are controlled, so the moment a customer pressed space the value came back
 * without it and the next letter landed against the previous word: "kolula
 * Solutions" could only be typed as "kolulaSolutions", and a street like
 * "Niederkassler Lohweg" as one word. Reported from production on the
 * guest-apartment flow.
 *
 * It survived because PASTING still works — the whole string arrives in one
 * change and its internal spaces are untouched — and because browser autofill
 * pastes. So everyone who filled the form the quick way saw it behave.
 *
 * Trimming belongs at the edges of the interaction, not inside it: the shared
 * `TextField` normalises on blur, and the validators trim what they read
 * anyway (`isPlausibleName`).
 *
 * The three PHONE reducers keep their trim: they read into a local to build
 * the `+` prefix, and a trailing space there is not a character the customer
 * is trying to type through.
 */
const recomputePersonalData = (state: BookingCreationState): void => {
  state.personalDataValidation = validatePersonalData(
    state.personalData,
    state.useCase,
    state.coveredBooking
  );
  state.nextEnabled = isPersonalDataStepValid(state);
};

/**
 * May the customer leave the shared personal-data step? The ONE gate for its
 * Next button: their own data against the required set for this use case and
 * coverage, and — when they book for someone else — that person's data too.
 *
 * It replaced four inline copies, two of which had drifted: switching back to
 * "myself" validated without the covered flag (Next stayed disabled on a
 * covered booking with no phone), and every recompute ignored the someone-else
 * fields (Next enabled with that person's name still empty).
 */
const isPersonalDataStepValid = (state: BookingCreationState): boolean =>
  isPersonalDataValid(
    state.personalDataValidation,
    state.useCase,
    state.coveredBooking
  ) &&
  (!state.isForSomeoneElse ||
    isSomeoneElseDataValid(state.someoneElseDataValidation, state.useCase));

const isSomeoneElseDataValid = (
  validation: SomeoneElseDataValidation,
  service: string | null
): boolean => {
  return validation.firstName && validation.lastName && validation.email;
};

const isRecipientDataValid = (validation: PersonalDataValidation): boolean => {
  // Require first name, last name, and email for recipient data
  return validation.firstName && validation.lastName && validation.email;
};

const isLandlordDataValid = (validation: PersonalDataValidation): boolean => {
  // For landlord step, we require first name, email, and phone
  return validation.firstName && validation.email && validation.phone;
};

const isSearchRoomDataValid = (
  validation: SearchRoomDataValidation
): boolean => {
  return (
    validation.city &&
    validation.district &&
    validation.checkInDate &&
    validation.checkOutDate &&
    validation.numberOfGuests
  );
};

interface SearchVenueDataValidation {
  city: boolean;
  district: boolean;
  checkInDate: boolean;
  checkOutDate: boolean;
  startTime: boolean;
  endTime: boolean;
  eventType: boolean;
  numberOfGuests: boolean;
}

const isSearchVenueDataValid = (v: SearchVenueDataValidation): boolean =>
  v.city &&
  v.district &&
  v.checkInDate &&
  v.checkOutDate &&
  v.startTime &&
  v.endTime &&
  v.eventType &&
  v.numberOfGuests;

/**
 * Recompute `nextEnabled` for the location/duration step from the full slice
 * state. Picks the right validator based on `state.useCase` so any reducer
 * that touches a relevant field (location, dates, times, eventType) can
 * uniformly call this and stay correct regardless of which field changed
 * last. Without this, venue-flow reducers reset `nextEnabled = false` and
 * never re-enable when the user fills fields in a different order.
 */
function isLocationDurationValid(state: BookingCreationState): boolean {
  const flow = spaceOf(flowUseCase(state.useCase))?.flow;
  if (flow === "stay") {
    return isSearchRoomDataValid({
      city: state.roomRentalLocation.city !== null,
      district: state.roomRentalLocation.district !== null,
      checkInDate: state.orderData.checkInDate !== null,
      checkOutDate: state.orderData.checkOutDate !== null,
      numberOfGuests: true,
    });
  }
  if (flow === "event") {
    const start = state.orderData.startTime;
    const end = state.orderData.endTime;
    return isSearchVenueDataValid({
      city: state.roomRentalLocation.city !== null,
      district: state.roomRentalLocation.district !== null,
      checkInDate: state.orderData.checkInDate !== null,
      checkOutDate: state.orderData.checkOutDate !== null,
      startTime: !!start && start !== "0",
      endTime: !!end && end !== "0",
      // Trimmed: a free-text name of spaces only names nothing.
      eventType: !!state.orderData.eventType?.trim(),
      numberOfGuests: true,
    });
  }
  return state.nextEnabled;
}

export const {
  setVoucherAmount,
  setVoucherCustomAmountIsValid,
  clearVoucherCustomAmount,
  clearVoucherSelectedAmount,
  completeStripePayment,
  numberOfGuestsSelectedClear,
  setVoucherCustomAmount,
  setVoucherSelectedAmount,
  bookingFlowChanged,
  setKeyRecipientPhase,
  setKeyPaymentModel,
  setPickupTimeSelected,
  setReturnTimeSelected,
  bookingCreationStarted,
  bookingCreationCanceled,
  navigatedToNextStep,
  navigatedToPreviousStep,
  dateSelected,
  dateSelectedClear,
  checkInDateSelected,
  checkInDateSelectedClear,
  checkOutDateSelected,
  checkOutDateSelectedClear,
  timeSelected,
  durationSelected,
  durationSelectedClear,
  packageSizeSelected,
  quantitySelected,
  quantity2Selected,
  quantitySelectedClear,
  assetCountSelected,
  assetGroupActivated,
  assetGroupDeactivated,
  assetGroupCountChanged,
  reservationCreated,
  reservationSnippetCreated,
  reservationUpdated,
  reservationRemoved,
  couponAppliedToReservations,
  setAppliedCouponCode,
  firstNameChanged,
  lastNameChanged,
  phoneChanged,
  setIsAddingAnotherEquipment,
  emailChanged,
  companyNameChanged,
  addressChanged,
  zipCodeChanged,
  cityChanged,
  countryChanged,
  recipientFirstNameChanged,
  recipientLastNameChanged,
  recipientPhoneChanged,
  recipientEmailChanged,
  // landlord data
  landlordFirstNameChanged,
  landlordLastNameChanged,
  landlordPhoneChanged,
  landlordEmailChanged,
  agreementChanged,
  newsletterConsentChanged,
  paymentTypeChanged,
  uscCustomerIdChanged,
  personalDataChanged,
  personalDataHydrated,
  personalDataDraftLoaded,
  accountProfilePrefilled,
  personalDataCleared,
  coveredBookingChanged,
  personalDataRevalidated,
  someoneElseFirstNameChanged,
  someoneElseLastNameChanged,
  someoneElseEmailChanged,
  couponRedemptionLoading,
  initializePaymentProvider,
  braintreePaymentInitialized,
  vippsPaymentInitialized,
  stripePaymentInitialized,
  noPaymentInitialized,
  paymentInitializationFailed,
  paymentInitializationRetryStarted,
  paymentCompletionBegan,
  paymentCompleted,
  subscriptionCompleted,
  paymentCompletionFailed,
  bookingStepChanged,
  locationDurationRevalidated,
  bookingCreationCompleted,
  setNextEnabled,
  setAvailabilities,
  setSUPAvailabilities,
  setSelectedLocation,
  setRoomRentalLocation,
  setSelectedGroup,
  setHasSelectedLocation,
  addAnotherGroup,
  setGroupsByLocation,
  groupsLoadStarted,
  groupsLoadFailed,
  setAssetGroup,
  setUseCase,
  addService,
  editService,
  removeService,
  setForSomeoneElse,
  setRoomRentalVoucher,
  setTotalPrice,
  setLoader,
  numberOfGuestsSelected,
  startTimeSelected,
  endTimeSelected,
  eventTypeSelected,
  coveringOrganizationSelected,
  privateBookingChosen,
  costCenterTyped,
  setKeyHandoverSelectedService,
  setVerifyPinResponseData,
  setVerificationPin,
  setSelectedParcelLocker,
  keyReturnFromDateSelected,
  keyReturnToDateSelected,
  setStep,
  setOrderComment,
} = bookingCreationSlice.actions;

export default bookingCreationSlice.reducer;
